import {
  authenticate,
  getSelf,
  register,
  googleConnect,
  generateBusinessMicrosoftUrl,
  generatePersonalMicrosoftUrl,
  generatePersonalMicrosoftReauthUrl,
  generatePersonalGoogleReauthUrl,
  generateDropboxUrl,
  generateBoxUrl,
  generateSalesforceUrl,
  generateEntraIdUrl,
  generatePersonalBoxReauthUrl,
  generatePersonalDropboxReauthUrl,
  entraIdConnect,
  groupConnect,
} from "@/services/api/app/auth";
import Store from "@/store/app/index";
import i18n from "@/boot/i18n";

let ENVIRONMENT = 'staging';
if (process.env.VUE_APP_ENVIRONMENT) {
  ENVIRONMENT = process.env.VUE_APP_ENVIRONMENT;
}

function setOsanoLocale(lang) {
  try {
    if (window.Osano?.cm) {
      window.Osano.cm.locale = lang;
    } else if (typeof window.Osano === 'function') {
      window.Osano('locale', lang);
    }
  } catch (error) {
    console.error('Error setting Osano locale:', error);
  }
}

const defaultState = () => ({
  self: {},
  isLoading: true,
  accessToken: null,
  googleTenant: {}
})

export default {
  namespaced: true,

  actions: {
    getSelf({commit}) {
      return getSelf()
        .then(user => {
          commit('setSelf', user);
          const lang = user.lang || 'en'
          i18n.locale = lang
          localStorage.setItem('lang', lang)
          setOsanoLocale(lang)
          return user;
        })
        .catch(() => {
          commit('resetState')
          return Promise.reject();
        })
    },
    register({commit}, registerForm) {
      return register(registerForm)
        .then((result) => {
          commit('setAccessToken', result.accessToken)
        })
    },
    authenticate({commit}, authenticateForm) {
      return authenticate(authenticateForm)
        .then(result => {
          commit('setAccessToken', result.accessToken)
          return result
        })
    },
    generateBusinessMicrosoftUrl({commit, dispatch}) {
      return generateBusinessMicrosoftUrl();
    },
    entraIdConnect({commit, dispatch}) {
      return entraIdConnect();
    },
    generatePersonalMicrosoftUrl({commit, dispatch}) {
      return generatePersonalMicrosoftUrl();
    },
    generatePersonalMicrosoftReauthUrl({commit, dispatch}) {
      return generatePersonalMicrosoftReauthUrl();
    },
    generatePersonalBoxReauthUrl({commit, dispatch}) {
      return generatePersonalBoxReauthUrl();
    },
    generatePersonalDropboxReauthUrl({commit, dispatch}) {
      return generatePersonalDropboxReauthUrl();
    },
    generatePersonalGoogleReauthUrl({commit, dispatch}) {
      return generatePersonalGoogleReauthUrl();
    },
    googleConnect({commit, dispatch}, data) {
      return googleConnect(data)
        .then(() => {
          return dispatch('getSelf')
        })
    },
    groupConnect({commit, dispatch}) {
      return groupConnect();
    },
    generateDropboxUrl({commit, dispatch}, levelType) {
      return generateDropboxUrl(levelType);
    },
    generateBoxUrl({commit, dispatch}, levelType) {
      return generateBoxUrl(levelType);
    },
    generateSalesforceUrl({commit, dispatch}, levelType) {
      return generateSalesforceUrl(levelType);
    },
    generateEntraIdUrl({commit, dispatch}, levelType) {
      return generateEntraIdUrl(levelType);
    },
    signOut({commit, state}, redirectEnabled = true) {
      if (ENVIRONMENT === 'production' && redirectEnabled) {
        if (state.self?.platform === "360") {
          window.location.href = "https://webapp.idrive360.com/api/v1/logout"
        } else if (state.self?.platform === "rpc") {
          window.location.href = "https://login.remotepc.com/rpcnew/logout"
        } else if (state.self?.platform === "rpc") {
          window.location.href = "https://login.remotepc.com/rpcnew/logout"
        } else if (state.self?.platform === "ibackup") {
          window.location.href = "https://www.ibackup.com/newibackup/logout"
        } else if (state.self?.platform === "c2c-reseller") {
          window.location.href = "https://reseller.idrive.com/app/logout"
        } else {
          window.location.href = 'https://www.idrive.com/idrive/logout'
        }
      } else {
        commit('resetState')
      }
    },
  },

  mutations: {
    setSelf(state, user) {
      state.self = user
    },
    setAccessToken(state, accessToken) {
      state.accessToken = accessToken
    },
    resetState(state) {
      Object.assign(state, defaultState())
    },
    getGoogleTenant(state, googleTenant) {
      state.googleTenant = googleTenant
    },
  },

  getters: {
    isMicrosoftProviderEnabled: (state) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === 1);
    },
    isGoogleProviderEnabled: (state) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === 2);
    },
    isDropboxProviderEnabled: (state) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === 3);
    },
    isBoxProviderEnabled: (state) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === 4);
    },
    isSalesforceProviderEnabled: (state) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === 5);
    },
    isEntraIdProviderEnabled: (state) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === 6);
    },
    isProviderConnected: (state) => (providerType) => {
      return !!state.self?.providerTenants?.find(providerTenant => providerTenant.providerTenantType === providerType);
    },
    isSuperAdmin: (state) => {
      return state.self.roles?.find(role => role.name === 'SuperAdmin')
    },
    accessLevel: (state) => {
      return state.self?.accessLevel;
    },
    providerLevelType: (state) => {
      if (Store.state.providerTenant === 'GOOGLE') {
        return state.self?.providerTenants?.find(providerTenant => providerTenant?.providerTenantType === 2)?.levelType;
      } else if (Store.state.providerTenant === 'MICROSOFT') {
        return state.self?.providerTenants?.find(providerTenant => providerTenant?.providerTenantType === 1)?.levelType;
      } else if (Store.state.providerTenant === 'DROPBOX') {
        return state.self?.providerTenants?.find(providerTenant => providerTenant?.providerTenantType === 3)?.levelType;
      } else if (Store.state.providerTenant === 'BOX') {
        return state.self?.providerTenants?.find(providerTenant => providerTenant?.providerTenantType === 4)?.levelType;
      } else if (Store.state.providerTenant === 'SALESFORCE') {
        return state.self?.providerTenants?.find(providerTenant => providerTenant?.providerTenantType === 5)?.levelType;
      } else if (Store.state.providerTenant === 'ENTRAID') {
        return state.self?.providerTenants?.find(providerTenant => providerTenant?.providerTenantType === 6)?.levelType;
      }
    }

  },

  state: defaultState()
}
